import { useCallback, useContext, useEffect, useState } from "react";
import DocumentsTable from "../../../components/DocumentsSettings/DocumentsTable/DocumentsTable";
import SearchBar from "../../../components/DocumentsSettings/SearchBar/SearchBar";
import Title from "../../../components/DocumentsSettings/Title/Title";
import Icon from "../../../components/Icon/Icon";
import styles from "./Departments.module.css";

import Pagination from "../../../components/DocumentsSettings/DocumentsTable/Pagination/Pagination";

import api from "../../../api/api";
import CreateDepartmentModal from "../../../components/CreateDepartmentModal/CreateDepartmentModal";
import type { IGetAllDepartmentsResponse } from "../../../shared/types/departments.types";
import StatusModal from "../../../components/StatusModal/StatusModal";
import ConfirmActionModal from "../../../components/ConfirmActionModal/ConfirmActionModal";
import { LoaderContext } from "../../../shared/Contexts/Loader/LoaderContext";

const Departments = () => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filter, setFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);

  const { isLoading } = useContext(LoaderContext);

  const [isCreateDepartmentModalOpen, setIsCreateDepartmentModalOpen] =
    useState(false);
  const [isSuccessDeleteModalOpen, setIsSuccessDeleteModalOpen] =
    useState(false);
  const [isSuccessCreationModalOpen, setIsSuccessCreationModalOpen] =
    useState(false);
  const [
    isConfirmDeletePropertyModalOpen,
    setIsConfirmDeletePropertyModalOpen,
  ] = useState(false);
  const [deletedDepartments, setDeletedDepartments] = useState<string[]>([]);
  const [departments, setDepartments] =
    useState<IGetAllDepartmentsResponse | null>(null);

  const handleFetchDepartments = useCallback(async () => {
    try {
      const data = await api.getAllDepartments({
        search: filter,
        limit: 8,
        page: currentPage,
      });
      setDepartments(data);
    } catch (error) {
      console.log(error);
    }
  }, [filter, currentPage]);

  useEffect(() => {
    handleFetchDepartments();
  }, [handleFetchDepartments]);

  const handleCheckboxChange = (id: string): void => {
    if (selectedIds.includes(id)) {
      const newIds = selectedIds.filter((itemId) => itemId !== id);
      setSelectedIds(newIds);
    } else {
      const newIds = [...selectedIds, id];
      setSelectedIds(newIds);
    }
  };

  const handleCheckboxSelectAll = (): void => {
    if (departments?.data.length === 0 || departments === null) return;
    const allVisibleIds = departments.data.map((department) => department.id);
    const areAllSelected = allVisibleIds.every((id) =>
      selectedIds.includes(id)
    );

    if (areAllSelected) {
      setSelectedIds((prev) =>
        prev.filter((id) => !allVisibleIds.includes(id))
      );
    } else {
      const newIds = [...selectedIds, ...allVisibleIds];
      setSelectedIds(newIds);
    }
  };

  const titles = ["Departamento", "Documentos", "Fecha de creación"];

  const filteredRows = departments?.data.map((department) => {
    const date = new Date(department?.createdAt);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);

    const dateForTable = `${day} / ${month} / ${year}`;

    return {
      id: department.id,
      properties: [
        department.code,
        department.documentTypes.length,
        dateForTable,
      ],
    };
  });

  const handleCreateDepartment = async (name: string) => {
    try {
      const response = await api.createDepartment({ code: name });
      await handleFetchDepartments();
      setIsSuccessCreationModalOpen(true);

      if (!response) return;
    } catch (error) {
      console.log(error);
    }
  };
  const handleDeleteDepartments = async () => {
    if (!departments) return;
    try {
      const response = await api.deleteDepartments({ ids: selectedIds });

      if (response.success === true) {
        const deletedDepartments = departments.data
          .filter((department) => selectedIds.includes(department.id))
          .map((department) => department.code);

        setDeletedDepartments(deletedDepartments);
        await handleFetchDepartments();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const existingDepartmentsNames =
    departments && departments.data.map((department) => department.code);

  return (
    <>
      {" "}
      <div className={styles.container}>
        <Title title="Clasificación de Documentos" className={styles.title} />
        <p className={styles.description}>
          Encuentra los departamentos en los que se archivaran los documentos
          digitalizados
        </p>
        <div className={styles.actionsContainer}>
          <SearchBar
            handleSearch={setFilter}
            placeholder="Buscar departamento"
          />
          <div className={styles.buttonsContainer}>
            <button
              className={styles.actionButton}
              onClick={() => {
                setIsCreateDepartmentModalOpen(true);
              }}
            >
              Añadir departamento
              <Icon name="create-department" size={20} />
            </button>
            <button
              onClick={() => {
                setIsConfirmDeletePropertyModalOpen(true);
              }}
              className={styles.actionButton}
              disabled={selectedIds.length === 0}
            >
              Eliminar departamento
              <Icon name="trash-can" size={20} />
            </button>
          </div>
        </div>

        {departments && filteredRows?.length ? (
          <>
            <div className={styles.exportTableContainer}>
              <DocumentsTable
                titles={titles}
                documents={filteredRows}
                selectedIds={selectedIds}
                handleCheckboxChange={handleCheckboxChange}
                handleCheckboxSelectAll={handleCheckboxSelectAll}
              />
              <Pagination
                totalPages={departments.totalPages}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </>
        ) : (
          !isLoading && <>Datos no encontrados {":("}</>
        )}
      </div>
      <CreateDepartmentModal
        existingDepartmentsNames={existingDepartmentsNames}
        isOpen={isCreateDepartmentModalOpen}
        onRequestClose={() => {
          setIsCreateDepartmentModalOpen(false);
        }}
        handleCreateDepartment={handleCreateDepartment}
      />
      <StatusModal
        isOpen={isSuccessCreationModalOpen}
        onRequestClose={() => {
          setIsSuccessCreationModalOpen(false);
        }}
      >
        <p className={styles.statusModalText}>
          ¡Los departamento creado con éxito!
        </p>
      </StatusModal>
      <StatusModal
        icon={
          <Icon
            name="folder-with-trashcan"
            gradient={true}
            className={styles.modalIcon}
            size={110}
          />
        }
        isOpen={isSuccessDeleteModalOpen}
        onRequestClose={() => {
          setIsSuccessDeleteModalOpen(false);
          setDeletedDepartments([]);
        }}
      >
        <p className={styles.statusModalText}>
          La departamento {deletedDepartments.map((prop) => prop).join(", ")} se
          ha eliminado
        </p>
      </StatusModal>
      <ConfirmActionModal
        isOpen={isConfirmDeletePropertyModalOpen}
        onRequestClose={() => {
          setIsConfirmDeletePropertyModalOpen(false);
        }}
        modalTitle="Eliminar Departamento"
        modalText="¿Está seguro que desea eliminar este departamento?, no podrá eliminar un departamento si existen documentos archivados en él."
        cancelActionText="Cancelar"
        cancelActionHandler={() => {
          console.log("close");
          setIsConfirmDeletePropertyModalOpen(false);
        }}
        confirmActionText="Eliminar"
        confirmActionHandler={async () => {
          await handleDeleteDepartments();
          setIsConfirmDeletePropertyModalOpen(false);
          setIsSuccessDeleteModalOpen(true);
          setSelectedIds([]);
        }}
      />
    </>
  );
};

export default Departments;
